import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 3 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function Sidenav({
  color = "info",
  brand = "",
  brandName,
  routes,
  brandIcon,
  ...rest
}) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [userPinned, setUserPinned] = useState(true);
  const [isWide, setIsWide] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1200 : true
  );
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    openConfigurator,
  } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      const w = window.innerWidth;
      setIsWide(w >= 1200);
      if (w < 1000) {
        // 1000 altı: her zaman mini
        setMiniSidenav(dispatch, true);
        return;
      }
      if (w < 1200) {
        // 1000–1199: otomatik mini (ikon-only)
        setMiniSidenav(dispatch, true);
        return;
      }
      // ≥1200: pin durumuna göre ayarla (pinliyse geniş, pinli değilse mini)
      setMiniSidenav(dispatch, userPinned ? false : true);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location, userPinned]);

  // Hover ile geçici genişlemede doğru metni göstermek için yardımcılar
  const isHoverExpanded = !userPinned && !miniSidenav; // mini kapalı ama pinli değilse => hover ile genişlemiş demektir
  const menuToggleName = isHoverExpanded
    ? "Menüyü Sabitle"
    : miniSidenav
    ? "Menüyü Genişlet"
    : "Menüyü Küçült";
  const menuToggleIcon = isHoverExpanded ? (
    <Icon>push_pin</Icon>
  ) : (
    <Icon>{miniSidenav ? "menu_open" : "menu"}</Icon>
  );

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href }) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, route, href, key }) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key &&
              currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={2}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      onMouseEnter={() => {
        if (window.innerWidth >= 1200 && !userPinned && miniSidenav) {
          setMiniSidenav(dispatch, false);
        }
      }}
      onMouseLeave={() => {
        if (window.innerWidth >= 1200 && !userPinned && !miniSidenav) {
          setMiniSidenav(dispatch, true);
        }
      }}
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <MDBox pt={2.5} pb={0} px={3} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="left"
          justifyContent="left"
        >
          {(brand || brandIcon) && (
            <MDBox
              component="img"
              src={miniSidenav ? brandIcon || brand : brand}
              alt="Brand"
              width={miniSidenav ? "2rem" : "10rem"}
            />
          )}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List sx={{ pt: 0, mt: "auto" }}>
        <MDBox>
          <SidenavCollapse
            name={menuToggleName}
            icon={menuToggleIcon}
            noCollapse
            onClick={() => {
              // Eğer şu an hover ile geçici olarak genişlemişse (miniSidenav === false && userPinned === false),
              // tıklama 'pin' anlamına gelsin: geniş açık kalsın.
              if (!userPinned && !miniSidenav) {
                setMiniSidenav(dispatch, false);
                setUserPinned(true);
                return;
              }
              // Normal toggle davranışı
              const next = !miniSidenav;
              setMiniSidenav(dispatch, next);
              setUserPinned(!next);
            }}
          />
        </MDBox>

        <MDBox>
          <SidenavCollapse
            name="Ayarlar"
            icon={<Icon>settings</Icon>}
            noCollapse
            onClick={() => setOpenConfigurator(dispatch, !openConfigurator)}
          />
        </MDBox>

        <MDBox>
          <SidenavCollapse
            name="Çıkış Yap"
            icon={<Icon>logout</Icon>}
            noCollapse
            onClick={() => {
              window.localStorage.removeItem("authToken");
              window.localStorage.removeItem("userData");
              window.localStorage.removeItem("bannerSettings");
              window.localStorage.removeItem("userCookies");
              window.localStorage.removeItem("userMeData");
              window.localStorage.removeItem("userCookieSummary");
              window.localStorage.removeItem("integrationStatus");

              window.sessionStorage.removeItem("authToken");
              window.sessionStorage.removeItem("userData");
              window.sessionStorage.removeItem("bannerSettings");
              window.sessionStorage.removeItem("userCookies");
              window.sessionStorage.removeItem("userMeData");
              window.sessionStorage.removeItem("userCookieSummary");
              window.sessionStorage.removeItem("integrationStatus");

              navigate("/sign-in");
            }}
          />
        </MDBox>
      </List>
    </SidenavRoot>
  );
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandIcon: PropTypes.string,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
